import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../../../context/ProjectContext';
import { useTheme } from '../../../context/ThemeContext';
import {
  Folder,
  FolderOpen,
  Plus,
  Edit2,
  Trash2,
  Archive,
  ArchiveRestore,
  MessageSquare,
  Calendar,
  MoreVertical,
  X,
  AlertCircle,
  FileText,
  Upload,
  Download,
  Pin,
  PinOff,
  BookOpen,
  Brain,
  Share2,
  Users,
  Link as LinkIcon
} from 'lucide-react';
import InfoModal from '../../common/InfoModal';

const ProjectsList = () => {
  const {
    projects,
    loading,
    createProject,
    updateProject,
    deleteProject,
    archiveProject,
    unarchiveProject,
    fetchProjects,
    selectProject
  } = useProjects();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6'
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProjects(showArchived);
  }, [showArchived, fetchProjects]);

  const colorOptions = [
    { value: '#3B82F6', label: 'Blue' },
    { value: '#10B981', label: 'Green' },
    { value: '#F59E0B', label: 'Orange' },
    { value: '#EF4444', label: 'Red' },
    { value: '#8B5CF6', label: 'Purple' },
    { value: '#EC4899', label: 'Pink' },
    { value: '#6366F1', label: 'Indigo' },
    { value: '#14B8A6', label: 'Teal' }
  ];

  const activeProjects = projects.filter(p => !p.archived);
  const archivedProjects = projects.filter(p => p.archived);
  const displayedProjects = (showArchived ? archivedProjects : activeProjects).sort((a, b) => {
    // Sort pinned projects to the top
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    // Then sort by updated_at or created_at
    return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
  });

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }

    const result = await createProject(formData);

    if (result.success) {
      setShowCreateModal(false);
      setFormData({ name: '', description: '', color: '#3B82F6' });
    } else {
      setError(result.error || 'Failed to create project');
    }
  };

  const handleEditProject = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }

    const result = await updateProject(selectedProject.id, formData);

    if (result.success) {
      setShowEditModal(false);
      setSelectedProject(null);
      setFormData({ name: '', description: '', color: '#3B82F6' });
    } else {
      setError(result.error || 'Failed to update project');
    }
  };

  const handleDeleteProject = async () => {
    const result = await deleteProject(selectedProject.id);

    if (result.success) {
      setShowDeleteModal(false);
      setSelectedProject(null);
    } else {
      setError(result.error || 'Failed to delete project');
    }
  };

  const handleArchiveToggle = async (project) => {
    if (project.archived) {
      await unarchiveProject(project.id);
    } else {
      await archiveProject(project.id);
    }
    setActiveMenu(null);
  };

  const handleTogglePin = async (project) => {
    try {
      const { apiPost } = await import('../../../utils/api');
      const response = await apiPost(`/projects/${project.id}/toggle_pin`);

      if (response.ok) {
        // Refetch projects to get updated list
        await fetchProjects(showArchived);
      }
    } catch (err) {
      console.error('Failed to toggle pin:', err);
    }
    setActiveMenu(null);
  };

  const openEditModal = (project) => {
    setSelectedProject(project);
    setFormData({
      name: project.name,
      description: project.description || '',
      color: project.color || '#3B82F6'
    });
    setShowEditModal(true);
    setActiveMenu(null);
  };

  const openDeleteModal = (project) => {
    setSelectedProject(project);
    setShowDeleteModal(true);
    setActiveMenu(null);
  };

  const handleProjectClick = (project, e) => {
    // Navigate to project detail page
    e.stopPropagation();
    navigate(`/dashboard/projects/${project.id}`);
  };

  return (
    <div className={`flex-1 overflow-y-auto p-6 ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-dm-serif ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Projects
            </h1>
            <p className={`mt-1 text-sm font-rasa ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Organize your chats into projects with memory and context
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowGuide(true)}
              className="p-2 rounded-lg transition-colors bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
              title="Guide"
            >
              <BookOpen className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors font-rasa"
            >
              <Plus className="w-5 h-5" />
              New Project
            </button>
          </div>
        </div>

        {/* Toggle archived */}
        <div className="mt-4 flex items-center gap-4">
          <button
            onClick={() => setShowArchived(false)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-rasa ${
              !showArchived
                ? 'bg-blue-500 text-white'
                : theme === 'dark'
                ? 'bg-gray-800 text-gray-300 hover:bg-gray-750'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FolderOpen className="w-4 h-4" />
            Active
          </button>
          <button
            onClick={() => setShowArchived(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-rasa ${
              showArchived
                ? 'bg-blue-500 text-white'
                : theme === 'dark'
                ? 'bg-gray-800 text-gray-300 hover:bg-gray-750'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Archive className="w-4 h-4" />
            Archived
          </button>
        </div>
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="max-w-7xl mx-auto flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : displayedProjects.length === 0 ? (
        <div className={`max-w-7xl mx-auto text-center py-12 ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`}>
          <Folder className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg">
            {showArchived ? 'No archived projects' : 'No projects yet'}
          </p>
          {!showArchived && (
            <p className="mt-2 text-sm">Create your first project to get started!</p>
          )}
        </div>
      ) : (
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedProjects.map((project) => (
            <div
              key={project.id}
              className={`relative rounded-lg border p-6 transition-all hover:shadow-lg ${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700 hover:border-gray-600'
                  : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
            >
              {/* Menu button */}
              <div className="absolute top-4 right-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveMenu(activeMenu === project.id ? null : project.id);
                  }}
                  className={`p-1 rounded-lg transition-colors ${
                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <MoreVertical className="w-5 h-5" />
                </button>

                {/* Dropdown menu */}
                {activeMenu === project.id && (
                  <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg border z-10 ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}>
                    <button
                      onClick={() => openEditModal(project)}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-left transition-colors font-rasa ${
                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                      }`}
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleTogglePin(project)}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-left transition-colors font-rasa ${
                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                      }`}
                    >
                      {project.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                      {project.pinned ? 'Unpin' : 'Pin'}
                    </button>
                    <button
                      onClick={() => handleArchiveToggle(project)}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-left transition-colors font-rasa ${
                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                      }`}
                    >
                      {project.archived ? (
                        <>
                          <ArchiveRestore className="w-4 h-4" />
                          Unarchive
                        </>
                      ) : (
                        <>
                          <Archive className="w-4 h-4" />
                          Archive
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => openDeleteModal(project)}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-left text-red-500 transition-colors font-rasa ${
                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                      }`}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>

              {/* Project content */}
              <button
                onClick={(e) => handleProjectClick(project, e)}
                className="w-full text-left"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div
                    className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
                    style={{ backgroundColor: project.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {project.pinned && <Pin className="h-4 w-4 text-orange-500 fill-orange-500 flex-shrink-0" />}
                      <h3 className="text-lg font-dm-serif truncate">
                        {project.name}
                      </h3>
                    </div>
                    {project.description && (
                      <p className={`text-sm line-clamp-2 font-rasa ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {project.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Project stats */}
                <div className={`flex items-center gap-4 text-sm font-rasa ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  <div className="flex items-center gap-1">
                    <MessageSquare className="w-4 h-4" />
                    <span>{project.chats_count || 0} chats</span>
                  </div>
                  {project.last_activity && (
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(project.last_activity).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className={`w-full max-w-md rounded-lg shadow-xl ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h3 className="text-lg font-dm-serif">
                {showCreateModal ? 'Create New Project' : 'Edit Project'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setError(null);
                  setFormData({ name: '', description: '', color: '#3B82F6' });
                }}
                className={`p-1 rounded-lg transition-colors ${
                  theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={showCreateModal ? handleCreateProject : handleEditProject} className="px-6 py-4 space-y-4">
              {error && (
                <div className="px-4 py-3 rounded-lg bg-red-500 bg-opacity-10 border border-red-500 text-red-500 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-rasa mb-2">Project Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter project name"
                  className={`w-full px-4 py-2 rounded-lg border transition-colors font-rasa ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 focus:border-blue-500 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 focus:border-blue-500 text-gray-900 placeholder-gray-500'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50`}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-rasa mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional project description"
                  rows={3}
                  className={`w-full px-4 py-2 rounded-lg border transition-colors resize-none font-rasa ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 focus:border-blue-500 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 focus:border-blue-500 text-gray-900 placeholder-gray-500'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50`}
                />
              </div>

              <div>
                <label className="block text-sm font-rasa mb-2">Color</label>
                <div className="space-y-3">
                  <div className="grid grid-cols-8 gap-2">
                    {colorOptions.map(color => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                        className={`w-8 h-8 rounded-lg transition-all ${
                          formData.color === color.value
                            ? 'ring-2 ring-offset-2 ring-blue-500'
                            : 'hover:scale-110'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.label}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-rasa">Custom color:</label>
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                      className="w-12 h-8 rounded cursor-pointer"
                    />
                    <span className="text-xs font-rasa text-gray-500 dark:text-gray-400">
                      {formData.color.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setError(null);
                    setFormData({ name: '', description: '', color: '#3B82F6' });
                  }}
                  className={`px-4 py-2 rounded-lg border transition-colors font-rasa ${
                    theme === 'dark'
                      ? 'border-gray-600 hover:bg-gray-700'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-rasa"
                >
                  {loading ? 'Saving...' : showCreateModal ? 'Create Project' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className={`w-full max-w-md rounded-lg shadow-xl ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`px-6 py-4 border-b ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h3 className="text-lg font-dm-serif">Delete Project</h3>
            </div>

            <div className="px-6 py-4">
              <div className="flex items-start gap-3 mb-4">
                <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-rasa mb-2">
                    Are you sure you want to delete "{selectedProject.name}"?
                  </p>
                  <p className={`text-sm font-rasa ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    This will permanently delete the project and all its associated data. Chats will remain but will no longer be linked to this project.
                  </p>
                </div>
              </div>

              {error && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-red-500 bg-opacity-10 border border-red-500 text-red-500 text-sm">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedProject(null);
                    setError(null);
                  }}
                  className={`px-4 py-2 rounded-lg border transition-colors font-rasa ${
                    theme === 'dark'
                      ? 'border-gray-600 hover:bg-gray-700'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteProject}
                  disabled={loading}
                  className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-rasa"
                >
                  {loading ? 'Deleting...' : 'Delete Project'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close menu */}
      {activeMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setActiveMenu(null)}
        />
      )}

      {/* Guide Modal */}
      <InfoModal
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
        title="Projects Guide"
      >
        <div className="space-y-4">
          <section>
            <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
              <Folder className="w-4 h-4" />
              What are Projects?
            </h4>
            <p className="mb-2">Projects help you organize related chats with intelligent memory and context management. The AI remembers conversations across all chats in a project.</p>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Creating a Project
            </h4>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Click <strong>"New Project"</strong> and give it a name, description, and color</li>
              <li>Add existing chats to a project using the chat menu (⋮)</li>
              <li>Start new chats directly from within a project</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Project Documents
            </h4>
            <p className="mb-2">Upload documents to a project so JPRO can reference them across all chats in that project.</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Upload files directly to a project (PDF, DOCX, XLSX, CSV)</li>
              <li>JPRO references these documents whenever you ask a question in that project</li>
              <li>No need to re-upload the same file in every chat</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
              <Brain className="w-4 h-4 text-blue-500" />
              Project Memory
            </h4>
            <p className="mb-2">JPRO remembers your conversations across all chats in a project.</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Start a new chat and JPRO already knows what was discussed in previous ones</li>
              <li>No need to repeat yourself — decisions and key topics carry over</li>
              <li>The more you use a project, the better JPRO understands the context</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Inviting Team Members
            </h4>
            <p className="mb-2">Collaborate on a project by inviting others to join.</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Open a project and click <strong>"Invite"</strong></li>
              <li>Send an invite by <strong>email</strong> or generate a <strong>shareable link</strong></li>
              <li>Choose a role when inviting:
                <ul className="list-disc list-inside space-y-1 ml-4 mt-1">
                  <li><strong>Viewer</strong> — can see chats and documents (read-only)</li>
                  <li><strong>Member</strong> — can chat and edit within the project</li>
                  <li><strong>Editor</strong> — can chat, edit, and invite others</li>
                </ul>
              </li>
              <li>Invite links expire after 7 days</li>
              <li>View and manage members from the <strong>"Members"</strong> button in the project</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
              <Share2 className="w-4 h-4" />
              Sharing Chats
            </h4>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Click the chat menu (⋮) to share a conversation</li>
              <li><strong>Export as PDF</strong> — download the chat as a document</li>
              <li><strong>Copy to Clipboard</strong> — paste the conversation into an email or document</li>
              <li><strong>Add to Project</strong> — move a standalone chat into a project so the team can see it</li>
              <li><strong>Move to Project</strong> — transfer a chat from one project to another</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Team Chat
            </h4>
            <p className="mb-2">Communicate with your team without leaving your project.</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>From a project page, click the <strong>"Team Chat"</strong> button in the header</li>
              <li>From inside any project chat, click the blue chat bubble in the bottom-right corner</li>
              <li>The drawer slides in showing your team conversations</li>
              <li>Select a conversation to send messages with full real-time support</li>
              <li>Click the expand icon to open team chat as a full page</li>
              <li>Close the drawer to return to your work</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
              <Folder className="w-4 h-4" />
              Managing Projects
            </h4>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Pin important projects so they appear at the top</li>
              <li>Archive completed projects to keep your workspace clean</li>
              <li>Use colors to visually organize your projects</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Example Use Cases</h4>
            <div className="space-y-2">
              <p className="italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                <strong>Property:</strong> Upload rent rolls and inspection reports, then chat about that property across multiple conversations
              </p>
              <p className="italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                <strong>Compliance Review:</strong> Keep all conversations and documents for a review organized in one place
              </p>
              <p className="italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                <strong>Client Work:</strong> Group all chats and files for a specific client together
              </p>
            </div>
          </section>
        </div>
      </InfoModal>
    </div>
  );
};

export default ProjectsList;
